import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { TrendingUp, BarChart3 } from "lucide-react";
import { TopPicksPerformanceModal } from "./TopPicksPerformanceModal.tsx";

export function TopPickBanner({ topPickInfo, loading, hideTopPickBanner, selectedTicker, handleAnalyzeSingle }: any) {
  const { t } = useTranslation();
  const [isPerformanceOpen, setIsPerformanceOpen] = useState(false);

  if (hideTopPickBanner || selectedTicker) {
    return null;
  }

  return (
    <>
      {topPickInfo && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-4 sm:p-6 bg-gradient-to-r from-indigo-900/40 to-zinc-900 border border-indigo-500/30 rounded-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-2xl relative overflow-hidden group cursor-pointer min-h-[124px] sm:min-h-[98px]"
          onClick={() => handleAnalyzeSingle(topPickInfo.ticker, false, true)}
        >
          <div className="absolute top-0 right-0 p-1 bg-indigo-500/20 text-indigo-300 text-[8px] uppercase tracking-widest font-bold hidden sm:block">
            {t("analysis.todays_selection")}
          </div>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-600/20 border border-indigo-500/50 rounded-sm flex items-center justify-center text-indigo-400 group-hover:scale-110 transition-transform shrink-0">
              <TrendingUp size={20} className="sm:w-6 sm:h-6" />
            </div>
            <div>
              <span className="text-indigo-400 text-[9px] sm:text-[10px] font-bold uppercase tracking-widest block mb-0.5 truncate">
                🌟 {t("analysis.pick_of_the_day")}
              </span>
              <div className="flex items-baseline gap-2">
                <h3 className="text-xl sm:text-2xl font-black text-white tracking-tight leading-none uppercase truncate">
                  {topPickInfo.ticker}
                </h3>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end sm:justify-end gap-3 w-full sm:w-auto z-10">
            <span className="text-zinc-300 text-[10px] uppercase font-bold tracking-widest hidden md:block">
              {t("analysis.strong_buy_signal_detected")}
            </span>
            <button 
              onClick={(e) => { e.stopPropagation(); setIsPerformanceOpen(true); }}
              className="flex-1 sm:flex-none px-3 py-1.5 sm:px-4 sm:py-2 h-8 bg-zinc-950/80 hover:bg-zinc-900 border border-indigo-500/30 hover:border-indigo-500/50 text-indigo-300 hover:text-indigo-200 text-[10px] uppercase font-extrabold tracking-widest rounded-sm transition-all flex items-center justify-center gap-1.5 shadow-md whitespace-nowrap"
            >
              <BarChart3 size={12} className="shrink-0" />
              <span className="hidden sm:inline">Top Picks </span>Performance
            </button>
            <button className="flex-1 sm:flex-none px-4 py-1.5 sm:px-6 sm:py-2 h-8 bg-indigo-600 hover:bg-indigo-500 text-white text-[10px] uppercase font-extrabold tracking-widest rounded-sm transition-all shadow-[0_0_20px_rgba(79,70,229,0.3)] whitespace-nowrap flex items-center justify-center">
              {t("common.details")}
            </button>
          </div>
        </motion.div>
      )}

      {!topPickInfo && (
        <div 
          className="p-4 sm:p-6 bg-gradient-to-r from-indigo-950/20 to-zinc-900/30 border border-indigo-500/20 rounded-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-2xl relative overflow-hidden animate-pulse min-h-[124px] sm:min-h-[98px]"
          aria-hidden="true"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-600/20 border border-indigo-500/30 rounded-sm shrink-0" />
            <div className="space-y-1.5">
              <div className="w-28 h-2.5 bg-indigo-500/20 rounded-sm" />
              <div className="w-16 h-6 bg-zinc-800 rounded-sm" />
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 w-full sm:w-auto">
            <div className="hidden md:block w-36 h-3 bg-zinc-800 rounded-sm" />
            <div className="flex-1 sm:flex-none w-28 sm:w-36 h-8 bg-zinc-800/80 rounded-sm" />
            <div className="flex-1 sm:flex-none w-16 sm:w-20 h-8 bg-indigo-600/30 rounded-sm" />
          </div>
        </div>
      )}
      <TopPicksPerformanceModal 
        isOpen={isPerformanceOpen} 
        onClose={() => setIsPerformanceOpen(false)} 
        onAnalyzeTicker={(ticker: string) => {
          setIsPerformanceOpen(false);
          handleAnalyzeSingle(ticker, false, true);
        }}
      />
    </>
  );
}
